<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { BekostigingsType } from "@/repositories/activiteit.repository";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";
import { useProfileStore } from "@/stores/profile.store";
import { addToDataLayer } from "@/services/gtm.service";
import { formatCurrency } from "@/helpers/currency.helper";
import { scrollToFirstError } from "@/helpers/validation.helper";
import { useUrlSearchParams } from "@/helpers/url.helper";
import { Notifications, notify } from "@kyvg/vue3-notification";
import BestelLessenModal from "./components/BestelLessenModal.vue";
import BestelTabContentLayout from "./components/BestelTabContentLayout.vue";
import BestelDeelnemersForm from "./components/BestelDeelnemersForm.vue";
import BestelGegevensForm from "./components/BestelGegevensForm.vue";
import TabSet from "@/components/Tabs/TabSet.vue";
import Tab from "@/components/Tabs/Tab.vue";

const store = useSportactiviteitBestelStore();
const profileStore = useProfileStore();
const params = useUrlSearchParams();

const showLessenModal = ref(false);
const tabSetRef = ref<InstanceType<typeof TabSet> | null>(null);
const deelnemersFormRef = ref<InstanceType<typeof BestelDeelnemersForm> | null>(null);
const gegevensFormRef = ref<InstanceType<typeof BestelGegevensForm> | null>(null);
const tabsReady = ref(false);

const showDeelnemersStap = computed(() => !store.isAbonnement);
const deelnemersTabIndex = computed(() => 1);
const gegevensTabIndex = computed(() => (showDeelnemersStap.value ? 2 : 1));
const bedanktTabIndex = computed(() => (showDeelnemersStap.value ? 3 : 2));

const tab2Disabled = computed(() => !store.guestAccount && !profileStore.isLoggedIn);
const tab3Disabled = computed(() => {
    if (store.isAbonnement) {
        return tab2Disabled.value;
    }
    return tab2Disabled.value || store.aantalPersonen === 0;
});
const isExtraKostenAkkoordValid = computed(() => !store.activiteit?.ExtraKosten || store.boeking.ExtraKostenAkkoord);
const isAkkoordVoorwaardenValid = computed(() => store.boeking.AkkoordVoorwaarden);
const bedanktTabEnabled = ref(false);

const openLessenModal = () => {
    showLessenModal.value = true;
};

const closeLessenModal = () => {
    showLessenModal.value = false;
};

type MaximaalAantalInschrijving = {
    Naam?: string;
    EmailadresVriend?: string;
    Kind?: unknown;
};

const mobileTotalPrice = computed(() => {

    if (!store.activiteit) {
        return "";
    }

    if (store.activiteit.Bekostigingstype === BekostigingsType.GRATIS) {
        return "GRATIS";
    }

    if (store.activiteit.Bekostigingstype === BekostigingsType.OPLOCATIE) {
        return `${formatCurrency(store.alleKosten?.TotaalKosten ?? 0)} op locatie`;
    }

    if (store.activiteit.Bekostigingstype === BekostigingsType.CREDITS) {
        return String(store.alleKosten?.AantalInschrijvingen ?? 0);
    }

    if (store.activiteit.Bekostigingstype === BekostigingsType.ABONNEMENT) {
        return `${formatCurrency(store.alleKosten?.TotaalKosten ?? 0)} per ${store.abonnementPeriode}`;
    }

    return formatCurrency(store.alleKosten?.TotaalKosten ?? 0);
});

const logIn = () => {
    store.saveBoeking();
    window.location.href = `/umbraco/surface/layout/login?platform=${store.platformId}`;
};

const register = () => {
    store.saveBoeking();
    // Voeg huidige pagina-URL toe als return parameter
    const currentUrl = window.location.origin + window.location.pathname + window.location.search;
    const params = [
        `platform=${store.platformId}`,
        `url=${encodeURIComponent(currentUrl)}`,
        store.account.Voornaam ? `firstName=${encodeURIComponent(store.account.Voornaam)}` : "",
        store.account.Achternaam ? `lastName=${encodeURIComponent(store.account.Achternaam)}` : "",
        store.account.Emailadres ? `email=${encodeURIComponent(store.account.Emailadres)}` : ""
    ].filter(Boolean).join("&");
    window.location.href = `/umbraco/surface/layout/register?${params}`;
};

const passwordForgotten = () => {
    window.location.href = `/umbraco/surface/layout/passwordforgotten?platform=${store.platformId}`;
};

const continueAsGuest = async () => {
    if (store.isAbonnement) {
        return;
    }
    store.continueAsGuest();
    await nextTick();
    tabSetRef.value?.selectTab(deelnemersTabIndex.value);
};

const sendGtmEvent = (event: string) => {
    if (!store.activiteit) return;
    const totalPrice = store.alleKosten
        ? (store.alleKosten as any).TotaalKosten ?? null
        : Number(store.activiteit.Kosten || 0) + Number(store.activiteit.ServiceKosten || 0);
    const category = store.aanbieder?.Naam ?? null;
    addToDataLayer(event, {
        ecommerce: {
            currency: 'EUR',
            value: totalPrice,
            items: [{
                item_id: store.activiteit.SportmomentId.toString(),
                item_name: store.activiteit.Naam ?? '',
                currency: 'EUR',
                item_brand: store.platform?.Naam ?? '',
                item_category: category ?? '',
                price: Number(store.activiteit.Kosten || 0) + Number(store.activiteit.ServiceKosten || 0),
                quantity: store.aantalPersonen,
            }]
        }
    });
};

const goToGegevens = async () => {
    if (store.isAbonnement) {
        sendGtmEvent('begin_checkout');
        sendGtmEvent('add_shipping_info');
        tabSetRef.value?.selectTab(gegevensTabIndex.value);
        return;
    }

    const valid = await deelnemersFormRef.value?.validate();
    if (!valid) {
        scrollToFirstError();
        return;
    }
    sendGtmEvent('begin_checkout');
    sendGtmEvent('add_shipping_info');
    tabSetRef.value?.selectTab(gegevensTabIndex.value);
};

const goToBevestiging = async () => {
    if (!isExtraKostenAkkoordValid.value || !isAkkoordVoorwaardenValid.value) {
        return;
    }

    const valid = await gegevensFormRef.value?.validate();
    if (!valid) {
        scrollToFirstError();
        return;
    }

    await store.checkGolfstart();
    if (!store.isGolfstartAllowed) return;

    sendGtmEvent('add_payment_info');
    store.saveBoeking();
    const result = await store.submitBoeking();

    if (result.type === "payment") {
        window.location.href = result.url;
        return;
    }

    if (result.type === "success") {
        sendGtmEvent('purchase');

        const bedanktPaginaBaseUrl = (window as any)._sportactiviteitBedankpaginaUrl as string | undefined;
        const sportmomentId = store.activiteit?.SportmomentId || Number(store.activiteitId);

        if (bedanktPaginaBaseUrl && sportmomentId) {
            window.location.href = `${bedanktPaginaBaseUrl}?id=${sportmomentId}`;
            return;
        }

        // Fallback for local/dev edge cases where global URL is missing
        bedanktTabEnabled.value = true;
        await nextTick();
        tabSetRef.value?.selectTab(bedanktTabIndex.value);
    }

    if (result.type === "error") {
        if (store.maximaalAantalKeerSporten.length) {
            const deelnemers = (store.maximaalAantalKeerSporten as MaximaalAantalInschrijving[])
                .map((inschrijving) => {
                    if (!inschrijving.EmailadresVriend && !inschrijving.Kind) {
                        return "Jijzelf";
                    }

                    if (inschrijving.EmailadresVriend) {
                        return `${inschrijving.Naam} (${inschrijving.EmailadresVriend})`;
                    }

                    return inschrijving.Naam;
                })
                .filter(Boolean)
                .join(", ");

            notify({
                type: "error",
                title: "Boeken is niet mogelijk",
                text: `Maximum van ${store.activiteit?.MaximaalAantalKeerSporten} keer proefsporten bereikt.${deelnemers ? ` Sporter(s): ${deelnemers}.` : ""}`,
                duration: 30000,
            });
            return;
        }

        notify({
            type: 'error',
            title: 'Er is een fout opgetreden bij het afronden van de boeking',
            text: result.message,
            duration: 30000,
        });
    }
};

onMounted(async () => {
    // Vul accountgegevens uit URL als ze nog niet gezet zijn
    if (!profileStore.isLoggedIn) {
        const firstName = params.getParam("firstName");
        const lastName = params.getParam("lastName");
        const email = params.getParam("email");
        if (firstName && !store.account.Voornaam) store.account.Voornaam = firstName;
        if (lastName && !store.account.Achternaam) store.account.Achternaam = lastName;
        if (email && !store.account.Emailadres) {
            store.account.Emailadres = email;
            store.account.EmailadresConfirm = email;
        }
    }

    await store.initialize();
    tabsReady.value = true;
    await nextTick();
    if (store.hasRestoredBoeking && !tab3Disabled.value) {
        // Terugkeer van foutpagina of van inloggen/registreren: ga direct naar gegevens-tab (alleen als deze al bereikbaar is)
        tabSetRef.value?.selectTab(gegevensTabIndex.value);
    }
    else if (profileStore.isLoggedIn) {
        tabSetRef.value?.selectTab(showDeelnemersStap.value ? deelnemersTabIndex.value : gegevensTabIndex.value);
    }
});
</script>

<template>
    <Notifications position="top right" classes="custom-notifications" animation-type="velocity" aria-live="polite"
        aria-atomic="true" />
    <div class="tabs-top order-page mb-5 mb-lg-10">
        <div class="container">
            <div class="row">
                <div class="col-xl-10 offset-xl-1">
                    <div class="mb-4">
                        <TabSet v-if="tabsReady" ref="tabSetRef">
                            <Tab heading="Inloggen" :number="1">
                                <BestelTabContentLayout :activiteit="store.activiteit" :aanbieder="store.aanbieder"
                                    :locatie="store.locatie" :lessen="store.lessen || []" :boeking="store.boeking"
                                    :alle-kosten="store.alleKosten" :mobile-total-price="mobileTotalPrice"
                                    :sidebar-title="store.wieGaanErContentblockTitle" :show-mobile-sidebar="true"
                                    :sidebar-items="store.sidebarItems" @open-lessons-overview="openLessenModal">
                                    <template #action>
                                        <button v-if="!store.isAbonnement" type="button" class="btn btn-sm btn-primary"
                                            @click="continueAsGuest">Volgende</button>
                                    </template>

                                    <div class="col-md-12 p-0">
                                        <div class="profile-box">
                                            <h1 class="profile-box-title payment-title h4">Ik heb een profiel</h1>
                                            <button type="button"
                                                class="btn btn-primary btn-sm btn-block payment-page-button-primary"
                                                @click="logIn">
                                                <span class="icon icon-user"></span>
                                                Inloggen
                                            </button>
                                            <button type="button"
                                                class="btn btn-sm btn-link-info btn-block profile-box-button-ghost"
                                                @click="passwordForgotten">
                                                Wachtwoord vergeten?
                                            </button>
                                        </div>
                                    </div>
                                    <div class="col-md-12 p-0">
                                        <div class="new-here-info-container">
                                            <div class="new-here-info-box">
                                                <h2 class="new-here-info-box-title payment-title h4">Ik ben nieuw hier
                                                </h2>
                                                <p class="new-here-info-box-subtitle">Maak binnen 3 minuten een profiel
                                                    aan</p>
                                                <ul class="list-checked">
                                                    <li>Vind en beheer je boekingen</li>
                                                    <li>Boek voor je kind(eren)</li>
                                                    <li>Hulp via NOC*NSF Sport Supportteam</li>
                                                </ul>
                                                <button type="button"
                                                    class="btn btn-primary btn-sm btn-block payment-page-button-primary"
                                                    @click="register">
                                                    Maak een profiel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="!store.isAbonnement" class="col-md-12 p-0">
                                        <div class="no-account-booking-box">
                                            <h2 class="payment-title h4">Boeken zonder account</h2>
                                            <p>Je kan jouw boeking ook afronden zonder account</p>
                                            <button type="button" class="btn btn-sm btn-gray d-flex align-items-center"
                                                @click="continueAsGuest">
                                                Doorgaan zonder account
                                                <span class="icon icon-arrow-right ml-1"></span>
                                            </button>
                                        </div>
                                    </div>
                                </BestelTabContentLayout>
                            </Tab>

                            <Tab v-if="showDeelnemersStap" heading="Deelnemers" :number="2" :disabled="tab2Disabled">
                                <BestelTabContentLayout :activiteit="store.activiteit" :aanbieder="store.aanbieder"
                                    :locatie="store.locatie" :lessen="store.lessen || []" :boeking="store.boeking"
                                    :alle-kosten="store.alleKosten" :mobile-total-price="mobileTotalPrice"
                                    :sidebar-title="store.wieGaanErContentblockTitle"
                                    :is-loading="store.isLoadingKosten" :sidebar-items="store.sidebarItems"
                                    @open-lessons-overview="openLessenModal">
                                    <template #action>
                                        <button type="button" class="btn btn-sm btn-primary"
                                            @click="goToGegevens">Volgende</button>
                                    </template>
                                    <BestelDeelnemersForm ref="deelnemersFormRef" @volgende="goToGegevens" />
                                </BestelTabContentLayout>
                            </Tab>

                            <Tab heading="Gegevens" :number="showDeelnemersStap ? 3 : 2" :disabled="tab3Disabled">
                                <BestelTabContentLayout :activiteit="store.activiteit" :aanbieder="store.aanbieder"
                                    :locatie="store.locatie" :lessen="store.lessen || []" :boeking="store.boeking"
                                    :alle-kosten="store.alleKosten" :mobile-total-price="mobileTotalPrice"
                                    :sidebar-title="store.wieGaanErContentblockTitle"
                                    :sidebar-items="store.sidebarItems"
                                    :is-loading="store.isSubmitting || store.isLoadingKosten"
                                    @open-lessons-overview="openLessenModal">
                                    <template #action>
                                        <div>
                                            <button type="button" class="btn btn-sm btn-primary"
                                                :disabled="store.isSubmitting || !store.isGolfstartAllowed || !isExtraKostenAkkoordValid || !isAkkoordVoorwaardenValid || !store.isInschrijvingPeriodeActief"
                                                @click="goToBevestiging">
                                                <span v-if="store.isSubmitting"
                                                    class="spinner-border spinner-border-sm mr-1" role="status"
                                                    aria-hidden="true"></span>
                                                Bestelling afronden
                                            </button>
                                        </div>
                                    </template>
                                    <BestelGegevensForm ref="gegevensFormRef" @volgende="goToBevestiging"
                                        @terug="() => tabSetRef?.selectTab(showDeelnemersStap ? 1 : 0)" />
                                </BestelTabContentLayout>
                            </Tab>

                            <Tab heading="Bedankt" :number="showDeelnemersStap ? 4 : 3" :disabled="!bedanktTabEnabled">
                                <div class="card p-3 mt-3">
                                    <h2 class="h5 mb-2">Bedankt</h2>
                                    <p class="mb-0">Bedankt voor uw boeking. U ontvangt een bevestigingsmail.</p>
                                </div>
                            </Tab>
                        </TabSet>
                    </div>
                </div>
            </div>
        </div>

        <BestelLessenModal :show="showLessenModal" :lessen="store.lessen" :activiteit="store.activiteit"
            @close="closeLessenModal" />
    </div>
</template>
